/**
 * CloudLimitsPage - Admin page to configure cloud/download limits
 */

import { useEffect, useState } from "react";
import {
  Card,
  Form,
  InputNumber,
  Button,
  Typography,
  Space,
  message,
  Skeleton,
  Descriptions,
  Alert,
} from "antd";
import {
  getCloudConfig,
  updateCloudConfig,
  getDefaultCloudConfig,
} from "@/services/configService";
import type { CloudConfig } from "@/types";

const { Title, Text } = Typography;

export default function CloudLimitsPage() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<CloudConfig | null>(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const data = await getCloudConfig();
      setConfig(data);
      form.setFieldsValue({
        browserDownloadLimitMB: data.browserDownloadLimitMB,
      });
    } catch (error) {
      console.error("Failed to load config:", error);
      message.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: {
    browserDownloadLimitMB: number;
  }) => {
    try {
      setSaving(true);
      await updateCloudConfig(values);
      message.success("Configuration saved successfully");
      await loadConfig();
    } catch (error) {
      console.error("Failed to save config:", error);
      message.error("Failed to save configuration. Make sure you have admin privileges.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaults = getDefaultCloudConfig();
    form.setFieldsValue(defaults);
  };

  if (loading) {
    return <Skeleton active title={false} paragraph={{ rows: 8 }} />;
  }

  return (
    <div>
      <Title level={3}>Cloud Limits</Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        Configure storage limits and download thresholds for cloud operations.
      </Text>

      <Card title="Browser Download Limits" style={{ marginBottom: 24 }}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="Browser downloads are limited by memory and connection stability"
          description="For downloads exceeding the threshold, users will be prompted to use the Desktop App which handles large files more reliably."
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ maxWidth: 500 }}
        >
          <Form.Item
            name="browserDownloadLimitMB"
            label="Maximum Browser Download Size (MB)"
            rules={[
              { required: true, message: "Required" },
              { type: "number", min: 50, message: "Minimum 50 MB" },
              { type: "number", max: 2000, message: "Maximum 2000 MB (2 GB)" },
            ]}
            tooltip="Downloads larger than this will require the Desktop App"
          >
            <InputNumber
              min={50}
              max={2000}
              step={50}
              style={{ width: "100%" }}
              addonAfter="MB"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit" loading={saving}>
                Save Changes
              </Button>
              <Button onClick={handleReset}>Reset to Default</Button>
            </Space>
          </Form.Item>
        </Form>

        {config?.updatedAt && (
          <Descriptions
            style={{ marginTop: 24 }}
            size="small"
            column={1}
            bordered
          >
            <Descriptions.Item label="Last Updated">
              {config.updatedAt.toDate().toLocaleString()}
            </Descriptions.Item>
            {config.updatedBy && (
              <Descriptions.Item label="Updated By">
                {config.updatedBy}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Card>
    </div>
  );
}
